/**
 * draft-form.ts — Bản đang soạn trên màn nhân viên AI (tự lưu vào ai_bots).
 * Nhóm "Cách trả lời" (persona, model, công cụ, settings…) vào bản nháp → khách dùng sau khi Phát hành.
 * Nhóm "Vận hành" (bật/tắt, trực tuyến, kênh phụ trách, lịch & chế độ, số khách/ngày) có hiệu lực ngay khi lưu.
 */
import type { AiBotToolsJson, AiMode, AiToolName, OnlineStatus } from '@/hooks/use-ai'
import { withDefaults, type BotSettings } from './bot-settings'
import type { TrainBot, TrainBotInput } from './train-api'

export type DraftForm = {
  name: string
  avatarEmoji: string | null
  description: string | null
  enabled: boolean
  provider: string | null
  model: string | null
  personaPrompt: string
  playbookPrompt: string
  toolsJson: AiBotToolsJson | null
  channelAccountIds: string[]
  settings: BotSettings
  // ── Vận hành (hiệu lực ngay) ──
  onlineStatus: OnlineStatus
  scheduleEnabled: boolean
  scheduleStartHour: number | null
  scheduleEndHour: number | null
  scheduleDaytimeMode: string | null
  scheduleNighttimeMode: string | null
  defaultMode: AiMode
  maxConcurrentDaily: number
}

const asMode = (v: unknown): AiMode => (v === 'manual' || v === 'auto' ? v : 'suggest')

export function toDraftForm(bot: TrainBot): DraftForm {
  return {
    name: bot.name,
    avatarEmoji: bot.avatarEmoji,
    description: bot.description,
    enabled: bot.enabled,
    provider: bot.provider,
    model: bot.model,
    personaPrompt: bot.personaPrompt ?? '',
    playbookPrompt: bot.playbookPrompt ?? '',
    toolsJson: bot.toolsJson,
    channelAccountIds: bot.channelAccountIds ?? [],
    settings: withDefaults(bot.settings),
    onlineStatus: bot.onlineStatus ?? 'online',
    scheduleEnabled: !!bot.scheduleEnabled,
    scheduleStartHour: bot.scheduleStartHour ?? 18,
    scheduleEndHour: bot.scheduleEndHour ?? 8,
    scheduleDaytimeMode: bot.scheduleDaytimeMode ?? 'suggest',
    scheduleNighttimeMode: bot.scheduleNighttimeMode ?? 'auto',
    defaultMode: asMode(bot.defaultMode),
    // Bot cũ lưu 0 (chưa dùng) → 9999 như backend.
    maxConcurrentDaily: bot.maxConcurrentDaily && bot.maxConcurrentDaily > 0 ? Math.min(bot.maxConcurrentDaily, 9999) : 9999,
  }
}

/** Phần thay đổi → body PUT /ai/bots/:id. */
export function toInput(patch: Partial<DraftForm>): TrainBotInput {
  const { personaPrompt, playbookPrompt, ...rest } = patch
  return {
    ...rest,
    ...(personaPrompt !== undefined ? { personaPrompt: personaPrompt || null } : {}),
    ...(playbookPrompt !== undefined ? { playbookPrompt: playbookPrompt || null } : {}),
  } as TrainBotInput
}

const DEFAULT_TOOL = { enabled: true, guardrail: { categoryIds: [] as string[] } }

export function toolEnabled(tools: AiBotToolsJson | null, name: AiToolName): boolean {
  return tools?.[name]?.enabled ?? true
}
export function toolCategoryIds(tools: AiBotToolsJson | null, name: AiToolName): string[] {
  return tools?.[name]?.guardrail?.categoryIds ?? []
}
/** Kho tri thức nhân viên AI được tra ([] = mọi kho). Cấu hình cũ (chưa có knowledgeBaseIds) đọc categoryIds. */
export function toolKnowledgeBaseIds(tools: AiBotToolsJson | null): string[] {
  const g = tools?.search_knowledge?.guardrail
  return g?.knowledgeBaseIds ?? g?.categoryIds ?? []
}
/** Sửa một công cụ, giữ nguyên phần còn lại (nguồn dữ liệu, chỉ còn hàng…). */
export function patchTool(
  tools: AiBotToolsJson | null, name: AiToolName,
  patch: { enabled?: boolean; categoryIds?: string[]; knowledgeBaseIds?: string[] },
): AiBotToolsJson {
  const base: AiBotToolsJson = tools ?? { search_products: { ...DEFAULT_TOOL }, search_knowledge: { ...DEFAULT_TOOL } }
  const cur = base[name] ?? DEFAULT_TOOL
  if (name === 'search_knowledge') {
    // Giới hạn theo KHO: knowledgeBaseIds; categoryIds để rỗng (khoá cũ).
    return {
      ...base,
      search_knowledge: {
        enabled: patch.enabled ?? cur.enabled,
        guardrail: { categoryIds: [], knowledgeBaseIds: patch.knowledgeBaseIds ?? toolKnowledgeBaseIds(base) },
      },
    }
  }
  return {
    ...base,
    [name]: {
      enabled: patch.enabled ?? cur.enabled,
      guardrail: { categoryIds: patch.categoryIds ?? cur.guardrail.categoryIds },
    },
  }
}

export type SectionProps = {
  botId: string
  form: DraftForm
  set: (patch: Partial<DraftForm>) => void
  setSettings: (fn: (s: BotSettings) => BotSettings) => void
  canEdit: boolean
}
